import { chaveDaCompra, liquidoDe, linhaDe, podeAbater } from '@domain/finance'
import type { Transaction } from '@domain/finance'
import { dayAndMonth } from '@shared/date/civil'
import { formatBRL } from '@shared/money/format'

/**
 * O que a lista diz, numa dica, sobre o abatimento de uma linha: quem abateu
 * um gasto, ou para qual gasto uma entrada foi usada. Puro, sobre as
 * transações que a lista JÁ tem em memória — passar o mouse não pode custar
 * uma leitura por linha.
 *
 * O que não está em memória (a entrada de outro mês) é contado pelo
 * `entryCount` que o gatilho gravou, e dito: "e mais 1 de outro mês". Some
 * calada, e a dica mentiria sobre quem pagou.
 */

export type VinculosDoMes = {
  /** As entradas ligadas, por compra. */
  entradasPorCompra: Map<string, Transaction[]>
  porId: Map<string, Transaction>
}

export function vinculosDoMes(transacoes: readonly Transaction[]): VinculosDoMes {
  const entradasPorCompra = new Map<string, Transaction[]>()
  const porId = new Map<string, Transaction>()
  for (const t of transacoes) {
    porId.set(t.id, t)
    const vinculo = t.enrichment.offsetOf
    if (vinculo === null || !podeAbater(linhaDe(t))) continue
    entradasPorCompra.set(vinculo.purchaseKey, [
      ...(entradasPorCompra.get(vinculo.purchaseKey) ?? []),
      t,
    ])
  }
  return { entradasPorCompra, porId }
}

export type DicaDoAbatimento = { titulo: string; linhas: string[] }

function rotulo(t: Transaction): string {
  return `${t.source.description} · ${dayAndMonth(t.source.transactionDate)} · ${formatBRL(t.source.amountCents)}`
}

export function dicaDoAbatimento(t: Transaction, v: VinculosDoMes): DicaDoAbatimento | null {
  const vinculo = t.enrichment.offsetOf
  if (vinculo !== null) {
    const gasto = v.porId.get(vinculo.transactionId)
    return {
      titulo: 'Usada para abater',
      linhas: [gasto === undefined ? vinculo.description : rotulo(gasto)],
    }
  }

  const abatimento = t.enrichment.offset
  if (abatimento === null) return null
  // Únicas por id: a lista do mês é a união de competência e caixa.
  const entradas = [
    ...new Map(
      (v.entradasPorCompra.get(chaveDaCompra(t)) ?? []).map((e) => [e.id, e]),
    ).values(),
  ]
  const linhas = entradas.map(rotulo)
  const fora = abatimento.entryCount - entradas.length
  if (fora > 0) linhas.push(`e mais ${String(fora)} de outro mês`)
  const fica = -liquidoDe(t)
  if (fica > 0) linhas.push(`Fica com você ${formatBRL(fica)}`)
  return { titulo: 'Abatida por', linhas }
}

/** O gasto que as entradas zeraram: a linha dele sai riscada. */
export function totalmenteAbatida(t: Transaction): boolean {
  return t.enrichment.offset !== null && liquidoDe(t) === 0
}
